import React, { useCallback, useMemo } from 'react'
import { DayPicker, isMatch } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import * as Styled from './DatePicker.styles'
import { DayElementProps, DatePickerProps } from './DatePicker.types'
import { Tooltip } from '~components/Tooltip'
import { Caption } from './Caption'

/**
 * Element to render a single day in calendar
 */
const DayElement: React.FC<DayElementProps> = ({ day, tooltip }) => (
  <Styled.DayContainer>
    <Tooltip content={tooltip}>
      <span>{day}</span>
    </Tooltip>
  </Styled.DayContainer>
)

const DatePicker: React.FC<DatePickerProps> = ({
  bookedDays,
  disabledDays,
  onDayClick,
  footer,
  size = 'medium',
  selected,
  colors,
  showSelectedMonth = false,
}) => {
  const modifiers = useMemo(() => bookedDays && { booked: bookedDays.map(({ matcher }) => matcher) }, [bookedDays])

  const dayTooltip = useCallback(
    (day: Date) => {
      const bookedDayCell = bookedDays?.find(({ matcher }) => isMatch(day, [matcher]))
      let tooltipContent

      if (bookedDayCell?.tooltip) {
        tooltipContent = typeof bookedDayCell.tooltip === 'function' ? bookedDayCell.tooltip(day) : bookedDayCell.tooltip
      }

      return <DayElement day={day.getDate()} tooltip={tooltipContent} />
    },
    [bookedDays],
  )

  const formatters = useMemo(
    () => ({
      formatDay: dayTooltip,
    }),
    [dayTooltip],
  )

  return (
    <Styled.Container size={size} colors={colors}>
      <DayPicker
        mode="single"
        formatters={formatters}
        showOutsideDays={true}
        fixedWeeks={true}
        selected={selected}
        onDayClick={onDayClick}
        modifiers={modifiers}
        modifiersClassNames={{ booked: 'booked' }}
        disabled={disabledDays}
        footer={footer}
        components={{
          Caption,
        }}
        defaultMonth={showSelectedMonth ? selected : undefined}
        weekStartsOn={1}
      />
    </Styled.Container>
  )
}

export default DatePicker
